// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppVersion } from "@/hooks/use-app-version";

/**
 * Headless controller that surfaces a persistent "new version available" toast (with a Reload
 * button) when a newer build has been deployed — detected either by the version check or by a
 * stale dynamic-import failure after a deploy. Renders nothing itself.
 */
export function UpdateAvailableToast() {
  const { t } = useTranslation("back-office");
  const { updateAvailable } = useAppVersion();
  const shownRef = useRef(false);

  const promptReload = useCallback(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    toast.info(t("updateAvailable.message"), {
      id: "app-update-available",
      duration: Number.POSITIVE_INFINITY,
      action: {
        label: t("updateAvailable.reload"),
        onClick: () => window.location.reload(),
      },
    });
  }, [t]);

  useEffect(
    function promptWhenNewerBuildDetected() {
      if (updateAvailable) promptReload();
    },
    [updateAvailable, promptReload],
  );

  useEffect(
    function promptOnStaleChunkLoadFailure() {
      // After a deploy, old content-hashed chunks 404 — Vite fires this on a failed dynamic import,
      // a definitive signal that this tab is running outdated code.
      function onPreloadError() {
        promptReload();
      }
      window.addEventListener("vite:preloadError", onPreloadError);
      return () => window.removeEventListener("vite:preloadError", onPreloadError);
    },
    [promptReload],
  );

  return null;
}
